import { useEffect, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { blogActions, blogSelectors } from "../../../ducks/blog";
import { AppDispatch } from "../../../store";
import { TableHeaderItem, TablePagination, TableSortBy } from "../../../hooks/useTable";
import { Blog } from "../../../ducks/blog/types";
import { ToastType } from "../../atoms/Toast";
import { BlogCreateRequestBody } from "../../../core/api/requests/blog/create/types";
import { BlogEditRequestBody } from "../../../core/api/requests/blog/edit/types";
import { appActions } from "../../../ducks/app";
import { ClickableText } from "../../atoms/ClickableText";
import { useModalWithProtection } from "../../../hooks/useModalWithProtection";

export const useBlogPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const blogs = useSelector(blogSelectors.blogs) ?? [];
    // const selectedBlog = useSelector(blogSelectors.selectedBlog);
    const pageSize = useSelector(blogSelectors.pageSize);
    const currentPage = useSelector(blogSelectors.currentPage);
    const totalItems = useSelector(blogSelectors.totalItems);
    const totalPages = useSelector(blogSelectors.totalPages);

    const { toast, showToast, hideToast } = useBlogToast();

    // Single useEffect to handle all data fetching
    useEffect(() => {
        dispatch(blogActions.fetchBlogs.started({
            page: currentPage,
            page_size: pageSize,
            onComplete: undefined,
            onError: undefined,
            onSuccess: undefined,
        }));
    }, [dispatch, currentPage, pageSize]);

    // Cleanup effect to reset state when component unmounts
    // useEffect(() => {
    //     return () => {
    //         // Reset blog state when component unmounts to prevent pagination issues
    //         dispatch(blogActions.resetToInitialState());
    //     };
    // }, [dispatch]);

    // Modal logic
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const handleOpenDetailModal = useCallback((blog: Blog) => {
        setSelectedBlog(blog); // Ensure selectedBlog is set
        setIsDetailModalOpen(true);
        dispatch(blogActions.getBlogDetails.started({
            id: blog.id,
            onComplete: undefined,
            onError: undefined,
            onSuccess: undefined,
        }));
    }, [dispatch]);

    const handleCloseDetailModal = useCallback(() => {
        setIsDetailModalOpen(false);
    }, []);

    const tableHeaders: TableHeaderItem<Blog & { no: number }>[] = useMemo(() => [
        { id: 1, title: "No", dataKey: "no" },
        {
            id: 2,
            title: "Title",
            dataKey: "title",
            popupTitle: "Blog Title",
            dataMapFunction: (blog) => ({
                text: blog.title,
                onClick: () => handleOpenDetailModal(blog),
            }),
            customComponent: ClickableText,
        },
        { id: 3, title: "Author Name", dataKey: "author_name" },
        {
            id: 4,
            title: "Date",
            dataKey: "created_at",
            dataMapFunction: (data) => {
                const dateString = data.created_at;
                if (!dateString) return "N/A";

                try {
                    // Handle ISO format "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD"
                    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
                        const [datePart] = dateString.split(' ');
                        const [year, month, day] = datePart.split('-');
                        const monthNames = [
                            'January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'
                        ];
                        const result = `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
                        return result;
                    }

                    // Handle the specific API format "DD-MM-YYYY HH:mm:ss"
                    if (typeof dateString === 'string' && dateString.includes('-') && dateString.includes(':')) {
                        const [datePart] = dateString.split(' ');
                        const [day, month, year] = datePart.split('-');
                        const monthNames = [
                            'January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'
                        ];
                        const result = `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
                        return result;
                    }

                    // Fallback to standard Date parsing
                    const date = new Date(dateString);
                    if (isNaN(date.getTime())) {
                        return "Invalid Date";
                    }
                    const day = date.getDate();
                    const month = date.toLocaleDateString('en-US', { month: 'long' });
                    const year = date.getFullYear();
                    const result = `${day} ${month} ${year}`;
                    return result;
                } catch (error) {
                    return "Invalid Date";
                }
            }
        },
        {
            id: 5,
            title: "Publish",
            dataKey: "publish",
            dataMapFunction: (data) => data.publish ? "✓" : ""
        },
    ], [handleOpenDetailModal]);

    const blogsWithNo = blogs.map((item, idx) => ({
        ...item,
        no: (currentPage - 1) * pageSize + idx + 1,
    }));

    const fetchDataFunction = useCallback((sort: TableSortBy, pagination?: TablePagination) => {
        const fetchPage = pagination?.page ?? currentPage;
        const fetchPageSize = pagination?.size ?? pageSize;
        dispatch(blogActions.fetchBlogs.started({
            page: fetchPage,
            page_size: fetchPageSize,
            onComplete: undefined,
            onError: undefined,
            onSuccess: undefined,
        }));
    }, [dispatch, currentPage, pageSize]);

    // handle modal
    const { isOpen: isCreateModalOpen, openModal: handleOpenCreateModal, closeModal: handleCloseCreateModal } = useModalWithProtection();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
    const handleOpenEditModal = () => setIsEditModalOpen(true);
    const handleCloseEditModal = () => setIsEditModalOpen(false);

    // Add a handler to change page and fetch data
    const handlePageChange = (newPage: number) => {
        dispatch(blogActions.fetchBlogs.started({
            page: newPage,
            page_size: pageSize,
            onComplete: undefined,
            onError: undefined,
            onSuccess: undefined,
        }));
    };

    // Only show pagination if there's more than one page
    const hasPagination = totalPages > 1;

    const handleRowDelete = useCallback(
        (data: Blog) => {
            dispatch(
                appActions.showModal({
                    title: "Delete Confirmation",
                    subTitle: "Are you sure you want to delete?",
                    buttons: [
                        {
                            text: "Cancel",
                            variant: "border",
                            onClick: () => dispatch(appActions.hideModal()),
                        },
                        {
                            text: "Delete",
                            variant: "filled",
                            onClick: () => {
                                dispatch(appActions.hideModal());
                                dispatch(appActions.showLoading());
                                dispatch(
                                    blogActions.deleteBlog.started({
                                        id: data.id,
                                        onComplete: () => {
                                            dispatch(appActions.hideLoading());
                                            // Optionally refetch blogs here if not handled by saga
                                            dispatch(
                                                blogActions.fetchBlogs.started({
                                                    page: 1,
                                                    page_size: 10,
                                                })
                                            );
                                        },
                                    })
                                );
                            },
                        },
                    ],
                })
            );
        },
        [dispatch]
    );

    return {
        tableHeaders,
        blogs: blogsWithNo,
        fetchDataFunction,
        isCreateModalOpen,
        isEditModalOpen,
        isDetailModalOpen,
        handleOpenCreateModal,
        handleCloseCreateModal,
        handleCloseDetailModal,
        page: currentPage,
        pageSize,
        totalItems,
        hasPagination,
        handleRowDelete,
        handleCloseEditModal,
        handleOpenEditModal,
        selectedBlog,
        setSelectedBlog,
        handlePageChange,
        toast,
        showToast,
        hideToast,
        refetchBlogs: () => fetchDataFunction({ field: 'title', order: 'asc' }, { page: currentPage, size: pageSize }),
    };
};

export const useBlogToast = () => {
    const [toast, setToast] = useState<{
        message: string;
        type: ToastType;
        visible: boolean;
    }>({ message: "", type: "success", visible: false });

    const showToast = (message: string, type: ToastType = "success") => {
        setToast({ message, type, visible: true });
        setTimeout(() => {
            setToast((t) => ({ ...t, visible: false }));
        }, 2000);
    };

    const hideToast = () => setToast((t) => ({ ...t, visible: false }));

    return { toast, showToast, hideToast };
};

export const useBlogRegistration = (showToast: (msg: string, type?: ToastType) => void, refetchBlogs?: () => void, closeModal?: () => void) => {
    const dispatch = useDispatch<AppDispatch>();

    const handleRegisterBlog = useCallback(
        (
            data: BlogCreateRequestBody,
            onError: (message: string) => void,
            onSuccess?: (result: any) => void
        ) => {
            dispatch(blogActions.createNewBlog.started({
                title: data.title,
                content: data.content,
                author_name: data.author_name,
                publish: data.publish,
                image: data.image,
                onComplete: () => { },
                onSuccess: (result: any) => {
                    showToast("Blog created successfully!", "success");
                    setTimeout(() => {
                        if (refetchBlogs) refetchBlogs();
                        if (closeModal) closeModal();
                        if (onSuccess) onSuccess(result);
                    }, 1200);
                },
                onError: (_err: any) => {
                    showToast("Failed to create blog. Please try again.", "error");
                    if (onError) onError("Failed to create blog. Please try again.");
                },
            }));
        },
        [dispatch, showToast, refetchBlogs, closeModal]
    );

    return { handleRegisterBlog };
};

export const useBlogEditing = (
    showToast: (msg: string, type?: ToastType) => void,
    refetchBlogs?: () => void,
    closeModal?: () => void
) => {
    const dispatch = useDispatch<AppDispatch>();

    const handleEditBlog = useCallback(
        (
            data: BlogEditRequestBody,
            id: string,
            onError: (message: string) => void,
            onSuccess?: (result: any) => void
        ) => {
            return dispatch(
                blogActions.updateBlog.started({
                    ...data,
                    id,
                    onComplete: () => { },
                    onSuccess: (result: any) => {
                        showToast("Updated successfully!", "success");
                        setTimeout(() => {
                            if (refetchBlogs) refetchBlogs();
                            if (closeModal) closeModal();
                            if (onSuccess) onSuccess(result);
                        }, 1200);
                    },
                    onError: (_err: any) => {
                        showToast("Failed to update blog. Please try again.", "error");
                        if (onError) onError("Failed to update blog. Please try again.")
                    }
                })
            )
        }, [dispatch, showToast, refetchBlogs, closeModal]
    )

    return { handleEditBlog };
}